import React, { useState, useEffect } from "react";
import { X, Loader, CheckCircle } from "lucide-react";
import { useWorker } from "../context/WorkerContext";
import { toast } from "react-toastify";

const AgreementModal = ({ onClose, onSuccess }) => {
  const { user, setUser, fetchAgreementUrl, token } = useWorker();
  const [agreementUrl, setAgreementUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAgreement();
  }, []);

  const loadAgreement = async () => {
    setIsLoading(true);
    try {
      const url = await fetchAgreementUrl(user?.name || "");
      setAgreementUrl(url);
    } catch (err) {
      console.error("Failed to load agreement:", err);
      toast.error("Failed to load agreement");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptAgreement = async () => {
  setIsSubmitting(true);
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/lenskart/mark-agreement`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        userId: user.id,
        isAgreementSigned: true  // ✅ Now we pass the value
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to accept agreement");
    }

    setUser({ ...user, isAgreementSigned: true });
    onSuccess(true);

  } catch (err) {
    console.error("Error accepting agreement:", err);
    toast.error("Failed to accept agreement");
    onSuccess(false);
  } finally {
    setIsSubmitting(false);
  }
};

  const handleCancel = () => {
    onSuccess(false);
     window.location.reload();
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black bg-opacity-40 backdrop-blur-sm flex justify-center items-center px-2 sm:px-4">
      <div className="bg-gradient-to-br from-white via-[#f9f9ff] to-[#eef1ff] rounded-2xl shadow-xl border border-gray-100 w-full max-w-6xl h-[90vh] flex flex-col mx-2 sm:mx-4">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 bg-white/80">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white border border-indigo-200 rounded-full flex items-center justify-center shadow-sm">
              <span className="text-indigo-600 text-lg sm:text-xl">✍️</span>
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                Service Agreement
              </h3>
              <p className="text-xs sm:text-sm text-gray-600">
                Please read and accept the agreement to continue
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Agreement Content - Full height on mobile */}
        <div className="flex-1 overflow-hidden p-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-3" />
                <p className="text-gray-600">Loading agreement...</p>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm w-full h-full bg-white">
              <iframe
                src={agreementUrl}
                title="Service Agreement Document"
                className="w-full h-full border-0"
                onLoad={() => setIsLoading(false)}
                style={{ minHeight: '400px' }}
              />
            </div>
          )}
        </div>

        {/* Footer with Action Buttons */}
        <div className="p-4 sm:p-6 border-t border-gray-200 bg-white/80">
          <div className="space-y-3">
            {/* Info Message */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 flex items-start gap-2">
                <span className="text-lg">ℹ️</span>
                <span className="text-xs sm:text-sm">
                  By accepting this agreement, you agree to the terms and conditions 
                  of working with our platform. Please read the entire document carefully.
                </span>
              </p>
            </div>

            {/* Action Buttons - Stack on mobile */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCancel}
                disabled={isSubmitting}
                className="w-full sm:flex-1 py-3 px-4 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 shadow-sm focus:ring-2 focus:ring-gray-200 disabled:opacity-50 transition-all text-sm sm:text-base"
              >
                Cancel
              </button>
              
              <button
                onClick={handleAcceptAgreement}
                disabled={isSubmitting || isLoading}
                className="w-full sm:flex-1 py-3 px-4 rounded-xl font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-md focus:ring-4 focus:ring-green-200 disabled:opacity-50 flex items-center justify-center gap-2 transition-all text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <>
                    <Loader className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    <span>Accepting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>I Accept the Agreement</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;